/* eslint-disable @typescript-eslint/no-explicit-any */
import { Crop } from 'react-image-crop';
import { toInteger } from 'es-toolkit/compat';

import { FileWithPreview } from '@tock/coretypes';
import BaseComponent from '../BaseComponent';

interface Props {
  file: FileWithPreview;
  aspect?: number;
  onSubmit(file: FileWithPreview, crop: Crop): Promise<any> | void;
  minHeightPixels: number;
  onClose(): void;
  onShowError(error: string): any;
  minWidthPixels: number;
  render(innerProps: InnerProps): JSX.Element;
  validateAspect?: boolean;
}

interface InnerProps {
  handleImageLoaded(image: any): void;
  handleCropChange(crop: Crop, percentCrop: Crop): void;
  handleSubmit(): void;
  crop: Crop;
}

interface State {
  crop: Crop;
  percentCrop: Crop;
  naturalHeight?: number;
  naturalWidth?: number;
}

const BASE_COORDINATES = {
  x: 0,
  y: 0,
};

const SIZE_ALLOWANCE = 3;

export default class ImageCropLogic extends BaseComponent<Props, State> {
  static defaultProps = {
    minHeightPixels: 0,
    minWidthPixels: 0,
  };

  constructor(props: Props) {
    super(props);
    this.state = this.getCropState(0, 0);
  }

  handleImageLoaded(image: any) {
    const { minHeightPixels, minWidthPixels } = this.props;
    const { naturalHeight, naturalWidth } = image;
    if (
      naturalHeight < minHeightPixels - SIZE_ALLOWANCE ||
      naturalWidth < minWidthPixels - SIZE_ALLOWANCE
    ) {
      this.props.onShowError(
        `The image file is too small, the file must be at least ${minWidthPixels}x${minHeightPixels}`
      );
      this.props.onClose();
    } else {
      this.setState({
        naturalHeight,
        naturalWidth,
        ...this.getCropState(naturalHeight, naturalWidth),
      });
    }
  }

  getCropState(height: number, width: number) {
    const crop = this.makeAspectCrop(height, width);
    return {
      crop,
      percentCrop: {
        ...BASE_COORDINATES,
        height: toInteger((crop.height / 100) * height),
        width: toInteger((crop.width / 100) * width),
      },
    };
  }

  makeAspectCrop(imageHeight: number, imageWidth: number) {
    const { aspect } = this.props;

    if (!aspect) {
      return {
        ...BASE_COORDINATES,
        width: 100,
        height: 100,
      };
    }

    let pixelWidth = imageHeight * aspect;
    let pixelHeight = pixelWidth * (1 / aspect);
    while (pixelHeight > imageHeight || pixelWidth > imageWidth) {
      pixelHeight = pixelHeight * 0.75;
      pixelWidth = pixelWidth * 0.75;
    }
    return {
      ...BASE_COORDINATES,
      width: (pixelWidth / imageWidth) * 100,
      height: (pixelHeight / imageHeight) * 100,
    };
  }

  handleCropChange(crop: Crop, percentCrop: Crop) {
    if (this.props.aspect) {
      crop.aspect = this.props.aspect;
    }
    // prevents tapping the screen from making an offset image
    if (crop.width === 0 && crop.height === 0) {
      this.setState({
        crop: { ...crop, x: 0, y: 0, width: NaN, height: NaN },
        percentCrop: { ...percentCrop, x: 0, y: 0, width: NaN, height: NaN },
      });
    } else {
      this.setState({ crop, percentCrop });
    }
  }

  handleSubmit() {
    const { minHeightPixels, minWidthPixels, onSubmit, file, onShowError, aspect, validateAspect } =
      this.props;
    const { percentCrop, naturalHeight, naturalWidth, crop } = this.state;

    const height = toInteger(
      naturalHeight && percentCrop.height && (naturalHeight * percentCrop.height) / 100
    );
    const width = toInteger(
      naturalWidth && percentCrop.width && (naturalWidth * percentCrop.width) / 100
    );

    const equalApproximative = (num1: number, num2: number, epsilon: number) =>
      Math.abs(num1 - num2) < epsilon;

    const isNotCropped = Number.isNaN(crop.height) && Number.isNaN(crop.width && crop.width);

    if (width && width < minWidthPixels) {
      onShowError(`The selected crop doesn't meet minimum width of ${minWidthPixels}px`);
    } else if (height && height < minHeightPixels) {
      onShowError(`The selected crop doesn't meet minimum height of ${minHeightPixels}px`);
    } else if (
      validateAspect &&
      isNotCropped &&
      naturalHeight &&
      naturalWidth &&
      aspect &&
      !equalApproximative(naturalWidth / naturalHeight, aspect, 0.1)
    ) {
      onShowError(`Please click and drag to set a crop for your image.`);
    } else {
      const x = toInteger(naturalWidth && percentCrop.x && (naturalWidth * percentCrop.x) / 100);
      const y = toInteger(
        naturalHeight && percentCrop.y && (naturalHeight * percentCrop.y) / 100
      );
      onSubmit(file, {
        x,
        y,
        width,
        height,
      });
    }
  }

  render() {
    return this.props.render({
      handleCropChange: this.handleCropChange,
      handleImageLoaded: this.handleImageLoaded,
      handleSubmit: this.handleSubmit,
      crop: this.state.crop,
    });
  }
}
